/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import {
	type ComponentRef,
	Injectable,
	type ViewContainerRef,
	computed,
	inject,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { CbRoutesUtils } from "@app/@classes/routes/cb-routes-utils";
import { Supabase } from "@app/@classes/supabase";
import { DialogService } from "@app/widgets/base/dialog/dialog.service";
import { UserCursorComponent } from "@app/widgets/base/user-cursor/user-cursor.component";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { sha256 } from "js-sha256";
import {
	BehaviorSubject,
	Subject,
	debounceTime,
	takeUntil,
	throttleTime
} from "rxjs";
import { AuthStateService } from "../shared/auth-state.service";
import { AuthTenantService } from "../shared/auth-tenant.service";
import { AuthService } from "../shared/auth.service";
import { FeaturesService } from "../shared/features.service";
import { LoggerService } from "../shared/logger.service";

const CURSOR_ROOM = "users_cursors";
const CURSOR_TIMEOUT = 20 * 1000;

interface CursorStatus {
	user_id?: string | null;
	user_email?: string | null;
	user_name?: string | null;
	action: "enter" | "move" | "leave";
	last_update?: number;
	cursor?: {
		x: number;
		y: number;
	};
	url?: string;
}

@Injectable({
	providedIn: "root",
})
export class RealtimeCursorsService extends Supabase {
	private readonly loggerService: LoggerService = inject(LoggerService);
	private readonly tenantService: AuthTenantService = inject(AuthTenantService);
	private readonly authService: AuthService = inject(AuthService);
	private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
	private readonly authStateService: AuthStateService = inject(AuthStateService);
	private readonly router: Router = inject(Router);
	private readonly dialogService: DialogService = inject(DialogService);
	private featureService: FeaturesService = inject(FeaturesService);

	private readonly cursorComponentRefMap: Map<
		string,
		ComponentRef<UserCursorComponent>
	> = new Map<string, ComponentRef<UserCursorComponent>>();

	public readonly broadcastDestroy$ = new Subject<void>();

	private broadcastChannel?: RealtimeChannel;

	private _cursorStatus: CursorStatus = {
		action: "enter",
	};
	private _broadcastTrackStatus: CursorStatus[] = [];

	public updateCursor$ = new Subject<{ x: number; y: number }>();

	private vcr?: ViewContainerRef;

	private currentRoute$!: BehaviorSubject<string>;
	private currentDialog$!: BehaviorSubject<string>;

	private tenantId = computed(() => this.authStateService.currentTenantId());

	private inactivityDestroyInterval = setInterval(() => {
		for (const status of this._broadcastTrackStatus) {
			if (
				status.last_update &&
				Date.now() - status.last_update > CURSOR_TIMEOUT
			) {
				if (status.user_id && this.cursorComponentRefMap.has(status.user_id)) {
					this.cursorComponentRefMap.get(status.user_id)?.instance.remove();
				}
			}
		}
	}, 10 * 1000);

	constructor() {
		super();

		this.currentRoute$ = new BehaviorSubject<string>(this.router.url);
		this.currentRoute$
			.pipe(takeUntil(this.broadcastDestroy$), debounceTime(100))
			.subscribe((route) => {
				this.refreshBroadcast();
			});

		this.currentDialog$ = new BehaviorSubject<string>("");
		this.currentDialog$
			.pipe(takeUntil(this.broadcastDestroy$), debounceTime(100))
			.subscribe(() => {
				this.refreshBroadcast();
			});

		toObservable(this.authStateService.currentTenantId)
			.pipe(takeUntil(this.broadcastDestroy$))
			.subscribe((tenantId) => {
				if (!tenantId) {
					this.broadcastUnsubscribe();
				} else {
					if (this.broadcastChannel) return;
					this.refreshBroadcast();
				}
			});

		this.supabaseClient.auth.onAuthStateChange((event, session) => {
			if (!session?.user) {
				this.broadcastUnsubscribe();
			}
		});

		this.router.events
			.pipe(takeUntil(this.broadcastDestroy$))
			.subscribe((event) => {
				if (event instanceof NavigationEnd) {
					if (event.urlAfterRedirects === this.currentRoute$.value) return;
					this.currentRoute$.next(event.urlAfterRedirects);
				}
			});

		this.dialogService.dialogChanges
			.pipe(takeUntil(this.broadcastDestroy$))
			.subscribe((dialog) => {
				this.currentDialog$.next(this.dialogService.dialogTreeIdentifier);
			});
	}

	public get hasRealtime(): boolean {
		return (
			this.featureService.canSelect(["realtime"]) &&
			!!CbRoutesUtils.hasRealtimeCollaboration(this.router.url) &&
			!!this.authStateService.currentUserInfo()?.show_cursor &&
			!!this.tenantId
		);
	}

	private get currentRoom() {
		return `${CURSOR_ROOM}_${this.tenantId}_${this.currentRoute$.value}_${this.currentDialog$.value}`;
	}

	private get hashedCurrentRoom() {
		return sha256(this.currentRoom);
	}

	public setViewContainerRef(vcr: ViewContainerRef) {
		this.vcr = vcr;
	}

	private drawCursor(status: CursorStatus) {
		if (!this.vcr) throw new Error("ViewContainerRef not set");
		if (!status.user_name || !status.cursor || !status.user_id) return;

		if (this.cursorComponentRefMap.has(status.user_id)) {
			const componentRef = this.cursorComponentRefMap.get(status.user_id);

			if (componentRef) {
				componentRef.setInput("x", status.cursor.x);
				componentRef.setInput("y", status.cursor.y);
			}
			return;
		}

		const componentRef = this.vcr.createComponent(UserCursorComponent, {
			injector: this.vcr.injector,
		});

		componentRef.setInput("x", status.cursor.x);
		componentRef.setInput("y", status.cursor.y);
		componentRef.setInput("username", status.user_name);
		componentRef.instance.onDestroy.subscribe(() => {
			if (status.user_id) {
				this.cursorComponentRefMap.get(status.user_id)?.destroy();
			}
		});

		this.cursorComponentRefMap.set(status.user_id, componentRef);

		componentRef.instance.onDestroy.subscribe(() => {
			if (status.user_id) this.cursorComponentRefMap.delete(status.user_id);
		});
	}

	private destroyAllCursors() {
		for (const componentRef of this.cursorComponentRefMap.values()) {
			componentRef.instance.remove();
		}
		this.cursorComponentRefMap.clear();
	}

	private initBroadcast() {
		if (!this.hasRealtime) return;

		this.loggerService.logInfo("User entered the room", this.currentRoom);

		this.broadcastChannel = this.supabaseClient
			.channel(this.hashedCurrentRoom)
			.on("broadcast", { event: "cursor" }, (data) => {
				if (!data["payload"]) return;
				if (data["payload"].user_id === this.authStateService.currentUserId()) return;

				const index = this._broadcastTrackStatus.findIndex(
					(status) => status.user_id === data["payload"].user_id,
				);

				if (data["payload"].action === "leave") {
					this.cursorComponentRefMap
						.get(data["payload"].user_id)
						?.instance.remove();
					if (index > -1) {
						this._broadcastTrackStatus.splice(index, 1);
					}
					return;
				}

				if (data["payload"].action === "enter" || index === -1) {
					this._broadcastTrackStatus.push(data["payload"]);
				} else {
					this._broadcastTrackStatus[index] = data["payload"];
				}

				this.drawCursor(data["payload"]);
			})
			.on("presence", { event: "join" }, ({ key, newPresences }) => { })
			.on("presence", { event: "leave" }, ({ key, leftPresences }) => {
				this._cursorStatus = {
					user_id: this.authStateService.currentUserId(),
					action: "leave",
				};
				this.updateBroadcast();
			})
			.subscribe((status) => {
				if (status === "SUBSCRIBED") {
					this._cursorStatus = {
						user_id: this.authStateService.currentUserId(),
						action: "enter",
					};
					this.updateBroadcast();
				} else if (status === "CLOSED") {
					this._cursorStatus = {
						user_id: this.authStateService.currentUserId(),
						action: "leave",
					};
					this.updateBroadcast();
				}
			});

		this.updateCursor$
			.pipe(takeUntil(this.broadcastDestroy$), throttleTime(150))
			.subscribe((cursor) => {
				if (!this.broadcastChannel) return;
				if (!this.authStateService.currentUserId()) return;
				if (this._cursorStatus.cursor === cursor) return;

				if (!this.authStateService.currentUserInfo()?.show_cursor) {
					this.broadcastUnsubscribe();
					this.destroyAllCursors();
				}

				this._cursorStatus = {
					user_id: this.authStateService.currentUserId(),
					user_email: this.authStateService.currentUserInfo()?.email,
					user_name: this.authStateService.currentUserInfo()?.name,
					action: "move",
					last_update: Date.now(),
					cursor,
					url: this.currentRoute$.value,
				};

				this.updateBroadcast();
			});
	}

	private updateBroadcast() {
		if (!this.broadcastChannel) return;

		this.broadcastChannel.send({
			type: "broadcast",
			event: "cursor",
			payload: this._cursorStatus,
		});
	}

	private refreshBroadcast() {
		this.destroyAllCursors();
		this.broadcastUnsubscribe();
		this.initBroadcast();
	}

	private broadcastUnsubscribe() {
		this.broadcastChannel?.untrack(this._cursorStatus);
		this.broadcastChannel?.unsubscribe();
		this.broadcastChannel = undefined;
	}

	ngOnDestroy(): void {
		clearInterval(this.inactivityDestroyInterval);

		this.broadcastUnsubscribe();

		this.broadcastDestroy$.next();
		this.broadcastDestroy$.complete();
	}
}
